/**
 * 백엔드 `/api/promotion-schedules/*` 스펙에 대응하는 클라이언트 타입.
 * amondz-backend-monorepo의 apps/admin/src/promotion-schedule/dtos 와 1:1 대응.
 */

/** 계획 트랙 — 연간 프로모션 스케줄표의 계획 구획. */
export const PROMOTION_SCHEDULE_TRACK = {
  PROMOTION: 'PROMOTION',
  FLASH_DEAL: 'FLASH_DEAL',
  REGULAR_WEEKLY: 'REGULAR_WEEKLY',
  REGULAR_MONTHLY: 'REGULAR_MONTHLY',
  PRODUCT_UNION: 'PRODUCT_UNION',
  BRAND_UNIT: 'BRAND_UNIT',
} as const;

export type PromotionScheduleTrack = (typeof PROMOTION_SCHEDULE_TRACK)[keyof typeof PROMOTION_SCHEDULE_TRACK];

/**
 * 기획전 유형 — 트랙과는 다른 축(성격).
 * `PRODUCT_UNION` 이 트랙에도 있으나 별개 값이다 (트랙=밴드 위치, 유형=항목 배지).
 */
export const PROMOTION_SCHEDULE_TYPE = {
  BRAND_UNION: 'BRAND_UNION',
  PRODUCT_UNION: 'PRODUCT_UNION',
  BRAND_SOLO: 'BRAND_SOLO',
  NEW_PRODUCT: 'NEW_PRODUCT',
  LARGE_PROMOTION: 'LARGE_PROMOTION',
  TIME_DEAL: 'TIME_DEAL',
} as const;

export type PromotionScheduleType = (typeof PROMOTION_SCHEDULE_TYPE)[keyof typeof PROMOTION_SCHEDULE_TYPE];

/** 상태 — 시트의 "확정 전까지는 회색 처리" 규칙 승계. */
export const PROMOTION_SCHEDULE_STATUS = {
  PLANNED: 'PLANNED',
  CONFIRMED: 'CONFIRMED',
  CANCELED: 'CANCELED',
} as const;

export type PromotionScheduleStatus = (typeof PROMOTION_SCHEDULE_STATUS)[keyof typeof PROMOTION_SCHEDULE_STATUS];

/**
 * 스케줄에 연결된 쿠폰 요약 (AMZBM-86).
 *
 * 스케줄은 쿠폰을 **참조만** 한다 — 이름·할인·기간·상태는 `Coupon` 이 정본이라 서버가 join 해서 내려준다.
 * 기간을 함께 주는 이유는 스케줄 기간과 견줘볼 수 있게 하기 위함이다. 어긋나도 서버는 막지 않는다.
 */
/**
 * 연결된 기획전 요약.
 *
 * 스케줄은 기획전을 **참조만** 한다 — 이름·기간·상태는 `Event` 가 정본이라 서버가 join 해서 준다.
 * 기간을 함께 받는 이유는 화면에서 스케줄 기간과 견줘보기 위함이다(쿠폰과 같은 규약).
 */
export type PromotionScheduleEvent = {
  id: number;
  mainTitle: string;
  /** ISO datetime */
  startDate: string;
  endDate: string;
  /** 0: 비활성, 1: 활성, 2: 무기한 노출 */
  status: number;
};

export type PromotionScheduleCoupon = {
  id: number;
  name: string;
  /** 0: 금액할인, 1: %할인 (쿠폰 도메인 규약) */
  discountType: number;
  /** 할인 타입에 따른 수치 — %할인이면 율, 금액할인이면 원 */
  discountAmount: number;
  /** ISO datetime. 날짜 지정 쿠폰만 값이 있다 — 발급일 기준·무기한은 null */
  startDate: string | null;
  endDate: string | null;
};

export type PromotionSchedule = {
  id: number;
  track: PromotionScheduleTrack;
  title: string;
  /** ISO datetime — 종일 항목은 00:00:00 */
  startDate: string;
  /** ISO datetime — **inclusive**. 종일 항목은 23:59:59 */
  endDate: string;
  status: PromotionScheduleStatus;
  type: PromotionScheduleType | null;
  adminId: number | null;
  /** Admin join 결과. 담당자 미지정이거나 계정이 사라진 경우 null. */
  adminName: string | null;
  eventId: number | null;
  /** 연결된 기획전 (연결이 없거나 기획전이 지워졌으면 null — eventId 는 남을 수 있다) */
  event: PromotionScheduleEvent | null;
  /** 연결된 쿠폰 (없으면 빈 배열) */
  coupons: PromotionScheduleCoupon[];
  memo: string | null;
  createDate: string;
  updateDate: string;
};

/**
 * 주차 메타 — 서버가 폴라리스 주차 규약으로 계산해 내려준다.
 * 프론트는 계산하지 않고 그대로 그린다 (계층 간 규약 불일치 방지).
 */
export type PromotionScheduleWeekMeta = {
  year: number;
  /** 1~12 */
  month: number;
  /** 월 기준 주차 (1~N) */
  index: number;
  /** 예: "8월 2주차" */
  label: string;
  /** 주 시작일 — 월요일 (yyyy-MM-dd) */
  startDate: string;
  /** 주 종료일 — 일요일 (yyyy-MM-dd) */
  endDate: string;
};

/** 전사 공용 Holiday 테이블. 타임라인 배경 컨텍스트 레이어로만 사용한다. */
export type PromotionScheduleHoliday = {
  /** yyyy-MM-dd */
  date: string;
  memo: string;
};

/**
 * 담당자 필터의 "미지정" 센티널.
 * 어드민 id 는 1부터 시작하므로 0 을 안전하게 쓴다 (백엔드 상수와 동일한 값).
 */
export const PROMOTION_SCHEDULE_ADMIN_UNASSIGNED = 0;

export type PromotionSchedulesFindQuery = {
  /** yyyy-MM-dd */
  from: string;
  /** yyyy-MM-dd (inclusive) */
  to: string;
  /** 미지정 시 전체 트랙 */
  tracks?: PromotionScheduleTrack[];
  /** 미지정 시 전체 상태 */
  statuses?: PromotionScheduleStatus[];
  /** 담당자 어드민 id 목록. 미지정 시 전체. 담당자 없는 항목만 보려면 0 을 포함한다 */
  adminIds?: number[];
};

export type PromotionSchedulesFindResponse = {
  /** startDate ASC, id ASC */
  schedules: PromotionSchedule[];
  /** 요청 구간과 겹치는 모든 주 (첫/마지막 주는 구간 밖 날짜를 포함할 수 있다) */
  weeks: PromotionScheduleWeekMeta[];
  holidays: PromotionScheduleHoliday[];
};

export type PromotionScheduleCreateRequest = {
  track: PromotionScheduleTrack;
  title: string;
  startDate: string;
  endDate: string;
  /** 미지정 시 서버에서 PLANNED */
  status?: PromotionScheduleStatus;
  type?: PromotionScheduleType | null;
  adminId?: number | null;
  eventId?: number | null;
  /**
   * 연결할 쿠폰 id 목록.
   * **전달하면 그 목록으로 통째 교체**되고, 키를 생략하면 기존 연결이 유지된다(부분 수정).
   */
  couponIds?: number[];
  memo?: string | null;
};

/**
 * 부분 수정 — 전달한 키만 갱신된다.
 * nullable 필드는 `null` 을 명시하면 비워지고, 키를 생략하면 기존 값이 유지된다.
 */
export type PromotionScheduleUpdateRequest = Partial<PromotionScheduleCreateRequest>;

export type PromotionScheduleDeleteResponse = {
  id: number;
};

export const PROMOTION_SCHEDULE_LOG_OPERATION = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
} as const;

export type PromotionScheduleLogOperation =
  (typeof PROMOTION_SCHEDULE_LOG_OPERATION)[keyof typeof PROMOTION_SCHEDULE_LOG_OPERATION];

/**
 * 변경 이력 한 건 — **변경 시점의 전체 스냅샷**이다.
 * "무엇이 바뀌었나"는 인접 이력을 비교해 화면에서 계산한다 (`diffScheduleLogs`).
 */
export type PromotionScheduleLog = {
  /** Mongo ObjectId — 이력 저장소가 MongoDB 다 (log-worker 가 기록) */
  id: string;
  operation: PromotionScheduleLogOperation;
  /** **변경자** — null 이면 스크립트/백필 */
  adminId: number | null;
  adminName: string | null;
  track: PromotionScheduleTrack;
  title: string;
  startDate: string;
  endDate: string;
  status: PromotionScheduleStatus;
  type: PromotionScheduleType | null;
  /**
   * 그 시점의 **담당자**. 저장 키는 `snapshot.adminId` 지만 이 응답에서는
   * 위 `adminId`(변경자)와 충돌하지 않도록 `managerAdminId` 로 내려온다.
   */
  managerAdminId: number | null;
  managerAdminName: string | null;
  eventId: number | null;
  /** 그 시점에 연결돼 있던 쿠폰 id. 연결 도입(AMZBM-86) 전 이력은 빈 배열 */
  couponIds: number[];
  memo: string | null;
  createDate: string;
};

/* ────────────────────────────────────────────────────────────────────────────
 * 리스트 · 단건 · 엑셀 반출 (AMZBM-110 / AMZUWEB-682)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * 리스트 조회 필터.
 *
 * ⚠️ **타임라인 조회(`PromotionSchedulesFindQuery`)와 다르다.** 타임라인은 `from`·`to` 가
 * 필수(구간 전건을 그린다)지만 리스트는 **선택**이다 — 페이지네이션이라 전체 기간을 훑어도
 * 응답이 잘리지 않는다. 대신 기간을 주면 **판정은 타임라인과 같은 겹침**이다.
 *
 * 서버는 이 필터를 **리스트 조회와 엑셀 반출 양쪽에서 같은 함수로** 조립한다
 * (`libs/shared` 의 `buildPromotionScheduleWhere`). 그래서 화면 건수와 파일 건수가 갈라지지 않는다.
 */
export type PromotionScheduleListFilter = {
  /** yyyy-MM-dd */
  from?: string;
  /** yyyy-MM-dd (inclusive) */
  to?: string;
  tracks?: PromotionScheduleTrack[];
  statuses?: PromotionScheduleStatus[];
  types?: PromotionScheduleType[];
  /** 담당자 어드민 id. 미지정 항목만 보려면 0(`PROMOTION_SCHEDULE_ADMIN_UNASSIGNED`)을 포함한다 */
  adminIds?: number[];
  /** 기획전 연결 여부 */
  hasEvent?: boolean;
  /** 쿠폰 연결 여부 — **링크 테이블 기준**이다. memo 의 계획 표기는 연결이 아니다 */
  hasCoupon?: boolean;
  /** 항목명·메모 부분 일치 */
  keyword?: string;
};

/** 정렬 가능 필드 — 백엔드 `PROMOTION_SCHEDULE_LIST_SORT_FIELDS` 와 같은 집합이어야 한다 */
export const PROMOTION_SCHEDULE_LIST_SORT_FIELDS = [
  'id',
  'title',
  'startDate',
  'endDate',
  'createDate',
  'updateDate',
] as const;

export type PromotionScheduleListSortField = (typeof PROMOTION_SCHEDULE_LIST_SORT_FIELDS)[number];

/**
 * 리스트 기본 정렬 — **번호 내림차순**.
 *
 * 시작일이 아니라 번호인 이유: 리스트의 첫 쓰임이 *"방금 등록한 게 잘 들어갔나"* 인데,
 * 시작일 기준이면 방금 만든 항목이 기간에 따라 아무 데나 꽂혀 첫 화면에 안 보인다.
 * (타임라인은 여전히 시작일 고정이라 두 화면의 역할이 갈린다.)
 *
 * ⚠️ 서버의 같은 상수(`libs/shared` 의 `PROMOTION_SCHEDULE_LIST_DEFAULT_SORT_BY`)와 **같은 값**이어야 한다.
 * 어긋나면 화면은 이 값으로 그리고 서버는 저 값으로 정렬해, 첫 진입과 새로고침의 순서가 달라진다.
 */
export const PROMOTION_SCHEDULE_LIST_DEFAULT_SORT_BY: PromotionScheduleListSortField = 'id';

export const PROMOTION_SCHEDULE_LIST_DEFAULT_SORT_ORDER = 'DESC';

export type PromotionSchedulesListQuery = PromotionScheduleListFilter & {
  page?: number;
  pageSize?: number;
  sortBy?: PromotionScheduleListSortField;
  sortOrder?: 'ASC' | 'DESC';
};

/**
 * 리스트 응답.
 *
 * 항목은 타임라인과 **같은 `PromotionSchedule`** 이다 — 같은 데이터의 다른 투영에 다른 모양을
 * 주면 정합 버그가 난다. 다른 것은 봉투(총건수)뿐이고 `weeks`·`holidays` 는 오지 않는다.
 */
export type PromotionSchedulesListResponse = {
  /** 필터에 걸린 총 건수 (페이지네이션 전) */
  totalCount: number;
  promotionSchedules: PromotionSchedule[];
};

/**
 * 엑셀 반출 **요청** 응답.
 *
 * 🔴 파일이 오지 않는다. 서버는 큐에 등록만 하고 이 메일 주소를 돌려주며,
 * 파일은 excel-worker 가 만들어 메일로 보낸다.
 */
export type PromotionScheduleExcelDownloadResponse = {
  adminEmail: string;
};
